/**
 * The product's icons, named for what they mean in Cooori Speaking Coach:
 * `lesson`, `coach`, `cohort`, `signOut`. Every interface icon in every app
 * is drawn through `<Icon name="…" />`, so a concept changes glyph here, once,
 * and the Styleguide's icon page shows the whole vocabulary.
 *
 * Only Lucide glyphs (Product Brand Guide, Iconography): no app draws its own
 * icon or imports `lucide-react`. Two names may share a glyph when they mean
 * different things in different places (`close` and `clear`); within one app a
 * glyph should keep one meaning, so a reader learns it once.
 */
import { Activity, Archive, ArchiveRestore, ArrowDown, ArrowLeft, ArrowRight, ArrowRightLeft, ArrowUp, ArrowUpRight, AudioLines, Award, Ban, BookA, BookOpen, Braces, BriefcaseBusiness, Bug, Building2, CalendarClock, CalendarDays, CalendarPlus, Captions, CassetteTape, ChartColumn, ChartColumnBig, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, CircleAlert, CircleCheck, CircleCheckBig, CircleDot, CircleHelp, CircleStop, CircleUser, CircleX, Clock3, ClockAlert, Columns3, Copy, CornerDownRight, Download, Ear, Earth, ExternalLink, FileCode2, FileDown, FlaskConical, Filter, FilterX, Flag, Flame, Focus, FolderOpen, Frown, Gauge, Gavel, GitCompareArrows, GraduationCap, History, Info, Image, KeyRound, Languages, Laugh, Layers, Layers3, LayoutDashboard, LetterText, Unlink, ListChecks, ListChevronsDownUp, ListChevronsUpDown, ListRestart, LockKeyhole, LogOut, Mail, MailCheck, MailMinus, MailWarning, MailX, MapPin, Maximize2, Meh, Hourglass, Menu, MessageCircle, MessageSquareText, MessagesSquare, Mic, MicOff, Microscope, Milestone, Minus, Monitor, MonitorSmartphone, NotebookPen, PanelLeftClose, PanelLeftOpen, Pipette, PanelRightClose, Pause, Pencil, PhoneCall, PhoneOff, Play, Plus, Rabbit, RefreshCw, RotateCcw, RotateCw, ScanSquare, ScanText, ScrollText, Search, Send, Settings, SquareArrowOutUpRight, ShieldCheck, ShieldOff, ShieldUser, SkipBack, SkipForward, Sparkles, Smartphone, Square, Star, SunMoon, Tablet, ThumbsDown, ThumbsUp, Timer, Trash2, TriangleAlert, Trophy, Turtle, Volume2, VolumeX, Undo2, UserRound, UsersRound, Video, Wrench, X, } from 'lucide-react';
export const icons = {
    // The learner's journey
    mic: Mic,
    progress: ChartColumnBig,
    coach: CircleUser,
    lesson: MessagesSquare,
    trophy: Trophy,
    bonus: Sparkles,
    certificate: Award,
    streak: Flame,
    highlight: Sparkles,
    words: LetterText,
    finish: Flag,
    accent: Earth,
    // The call
    call: PhoneCall,
    endCall: PhoneOff,
    micOff: MicOff,
    video: Video,
    message: MessageCircle,
    providerTurn: ArrowRightLeft,
    providerRequest: SquareArrowOutUpRight,
    toolCall: Wrench,
    privacy: ShieldCheck,
    // Device environment
    devices: MonitorSmartphone,
    deviceDesktop: Monitor,
    deviceMobile: Smartphone,
    deviceTablet: Tablet,
    // The post-call survey: its questions, and the faces under its rating
    overallRating: Star,
    speakingSpeed: AudioLines,
    transcription: Captions,
    hearing: Ear,
    firstMinute: Timer,
    bestMoment: ThumbsUp,
    worstMoment: ThumbsDown,
    technicalIssue: Bug,
    noTechnicalIssues: Check,
    ratingLow: Frown,
    ratingMiddle: Meh,
    ratingHigh: Laugh,
    // Enrollment and time
    program: BookOpen,
    /** The public start guide for new students. */
    guide: BookOpen,
    /** A study period being set up, not yet open. */
    preparing: Hourglass,
    level: Layers,
    tier: Layers3,
    placement: MapPin,
    time: Clock3,
    date: CalendarDays,
    schedule: CalendarClock,
    overdue: ClockAlert,
    /** A past day of a study period with no practice (Progress calendar). */
    missedDay: CircleAlert,
    /** Pending practice reminder beside an active streak. */
    streakReminder: CircleAlert,
    newEnrollment: CalendarPlus,
    endNow: CircleStop,
    // People and organisations
    client: Building2,
    cohort: UsersRound,
    student: GraduationCap,
    hrUser: BriefcaseBusiness,
    admin: ShieldUser,
    account: UserRound,
    email: Mail,
    overview: LayoutDashboard,
    status: Activity,
    // Content
    theme: FolderOpen,
    vocabulary: BookA,
    teachingFrame: Focus,
    difficulty: Gauge,
    notes: NotebookPen,
    sourceFile: FileCode2,
    templateVariables: Braces,
    coverage: ChartColumn,
    analysis: Microscope,
    lab: FlaskConical,
    recording: CassetteTape,
    selectedTurn: ScanSquare,
    regressionCase: ScrollText,
    criteria: ListChecks,
    assessment: Gavel,
    evidence: ScanText,
    pickTurns: ScanText,
    pickRun: Pipette,
    missingEvidence: Unlink,
    metadata: Braces,
    language: Languages,
    appearance: SunMoon,
    history: History,
    auditLog: ScrollText,
    jobRun: Activity,
    compare: GitCompareArrows,
    current: CircleDot,
    nested: CornerDownRight,
    // Invitations and email
    send: Send,
    resend: RotateCw,
    invitePending: Mail,
    inviteAccepted: MailCheck,
    inviteExpired: MailMinus,
    inviteFailed: MailWarning,
    inviteSuppressed: MailX,
    // States
    info: Info,
    success: CircleCheck,
    warning: TriangleAlert,
    danger: CircleAlert,
    completed: CircleCheckBig,
    failed: CircleX,
    unknown: CircleHelp,
    locked: LockKeyhole,
    selected: Check,
    done: Check,
    indeterminate: Minus,
    stateTransition: Milestone,
    // Actions
    add: Plus,
    remove: Minus,
    edit: Pencil,
    settings: Settings,
    delete: Trash2,
    copy: Copy,
    download: Download,
    import: FileDown,
    refresh: RefreshCw,
    restart: RotateCcw,
    slowMotion: Turtle,
    fastMotion: Rabbit,
    rollback: RotateCcw,
    undo: Undo2,
    retire: Archive,
    restore: ArchiveRestore,
    resetView: ListRestart,
    resetPanels: Columns3,
    cancel: Ban,
    deactivate: ShieldOff,
    search: Search,
    filter: Filter,
    clearFilters: FilterX,
    close: X,
    clear: X,
    maximize: Maximize2,
    help: CircleHelp,
    feedback: MessageSquareText,
    signOut: LogOut,
    changePassword: KeyRound,
    // Media
    image: Image,
    play: Play,
    pause: Pause,
    volume: Volume2,
    volumeOff: VolumeX,
    stop: Square,
    skipBack: SkipBack,
    skipForward: SkipForward,
    // Navigation and disclosure
    back: ArrowLeft,
    forward: ArrowRight,
    goTo: ArrowUpRight,
    external: ExternalLink,
    previous: ChevronLeft,
    next: ChevronRight,
    breadcrumb: ChevronRight,
    expand: ChevronDown,
    more: Plus,
    collapse: ChevronUp,
    expandAll: ListChevronsUpDown,
    collapseAll: ListChevronsDownUp,
    choose: ChevronsUpDown,
    sortAscending: ArrowUp,
    sortDescending: ArrowDown,
    menu: Menu,
    sidebarOpen: PanelLeftOpen,
    sidebarClose: PanelLeftClose,
    panelClose: PanelRightClose,
};
/** The map's sections, in order: the Styleguide's icon page lists them. */
export const iconGroups = [
    {
        title: "The learner's journey",
        names: [
            'mic', 'progress', 'coach', 'lesson', 'trophy', 'bonus', 'certificate', 'streak',
            'highlight', 'words', 'finish', 'accent',
        ],
    },
    {
        title: 'The call',
        names: [
            'call', 'endCall', 'micOff', 'video', 'message', 'providerTurn', 'providerRequest', 'toolCall', 'privacy',
        ],
    },
    {
        title: 'Device environment',
        names: ['devices', 'deviceDesktop', 'deviceMobile', 'deviceTablet'],
    },
    {
        title: 'The post-call survey',
        names: [
            'overallRating', 'speakingSpeed', 'transcription', 'hearing', 'firstMinute',
            'bestMoment', 'worstMoment', 'technicalIssue', 'noTechnicalIssues', 'ratingLow', 'ratingMiddle', 'ratingHigh',
        ],
    },
    {
        title: 'Enrollment and time',
        names: [
            'program', 'guide', 'preparing', 'level', 'tier', 'placement', 'time', 'date', 'schedule', 'overdue', 'missedDay',
            'streakReminder', 'newEnrollment', 'endNow',
        ],
    },
    {
        title: 'People and organisations',
        names: [
            'client', 'cohort', 'student', 'hrUser', 'admin', 'account', 'email', 'overview', 'status',
        ],
    },
    {
        title: 'Content',
        names: [
            'theme', 'vocabulary', 'teachingFrame', 'difficulty', 'notes', 'sourceFile',
            'templateVariables', 'coverage', 'analysis', 'lab', 'recording', 'selectedTurn', 'regressionCase', 'criteria', 'assessment', 'evidence', 'pickTurns', 'pickRun', 'missingEvidence', 'metadata', 'language', 'appearance', 'history', 'auditLog', 'jobRun', 'compare',
            'current', 'nested',
        ],
    },
    {
        title: 'Invitations and email',
        names: [
            'send', 'resend', 'invitePending', 'inviteAccepted', 'inviteExpired', 'inviteFailed',
            'inviteSuppressed',
        ],
    },
    {
        title: 'States',
        names: [
            'info', 'success', 'warning', 'danger', 'completed', 'failed', 'unknown', 'locked',
            'selected', 'done', 'indeterminate', 'stateTransition',
        ],
    },
    {
        title: 'Actions',
        names: [
            'add', 'remove', 'edit', 'settings', 'delete', 'copy', 'download', 'import', 'refresh', 'restart',
            'rollback', 'undo', 'retire', 'restore', 'resetView', 'resetPanels', 'cancel', 'deactivate', 'search',
            'filter', 'clearFilters', 'close', 'clear', 'maximize', 'help', 'feedback',
            'signOut', 'changePassword',
        ],
    },
    {
        title: 'Media',
        names: [
            'image', 'play', 'pause', 'stop', 'slowMotion', 'fastMotion', 'skipBack', 'skipForward', 'volume', 'volumeOff',
        ],
    },
    {
        title: 'Navigation and disclosure',
        names: [
            'back', 'forward', 'goTo', 'external', 'previous', 'next', 'breadcrumb', 'expand', 'more',
            'collapse', 'expandAll', 'collapseAll', 'choose', 'sortAscending', 'sortDescending', 'menu', 'sidebarOpen',
            'sidebarClose', 'panelClose',
        ],
    },
];
//# sourceMappingURL=icons.js.map